/**
 * RequireStaff — the client-side PII boundary.
 *
 * THE GUARANTEE: with no staff token, the protected subtree NEVER MOUNTS. Not "mounts and hides",
 * not "mounts and shows a spinner" — `<Navigate>` is returned in place of `children`, so React
 * never renders them, so their `useQuery` hooks never run, so no PII is ever fetched, held in a
 * cache, or written to the DOM. That ordering is the whole design: a guard that renders its
 * children and then covers them has already put the data on the page.
 *
 * This is a client guard and therefore NOT the security boundary — it cannot be, since anyone can
 * edit the bundle they were served. The real boundary is the staff scheme on `/api/admin/*`,
 * which validates the `:staff` audience and rejects a candidate token outright, and behind that,
 * Cloudflare Access + the IP allowlist. This guard exists so that the app does not fetch PII it
 * has no business fetching, and so a signed-out reviewer sees a sign-in page rather than a
 * screenful of 401s. Three locks on the same door; this is the cheapest of the three, and the
 * other two do not excuse skipping it.
 *
 * `from` carries only a pathname. No admin route puts an email, an id or anything else personal
 * in the URL — the lookup screen keeps its query in component state for exactly this reason —
 * so there is nothing here to leak into history or a referrer.
 */
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStaffSession } from './session';
import { AppShell } from '@/components/AppShell';

export function RequireStaff({ children }: { children: ReactNode }) {
  const accessToken = useStaffSession((state) => state.accessToken);
  const location = useLocation();

  if (accessToken === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <AppShell>{children}</AppShell>;
}
